// src/pages/AccountPage.tsx
import React, { Suspense } from 'react';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';

// --- Komponent pomocniczy do stylowania p�l formularza ---
const FormInput = ({ name, type = 'text', label, placeholder }: any) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-300">{label}</label>
        <Field
            id={name}
            name={name}
            type={type}
            placeholder={placeholder || label}
            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);

// --- G��wny komponent strony ---
const AccountPageContent: React.FC = () => {
    const { logout } = useAuth();
    const { t } = useTranslation();
    const { showNotification, showConfirmation } = useNotification();

    // --- Schematy Walidacji ---
    const passwordValidationSchema = Yup.object({
        oldPassword: Yup.string().required(t('validation.required')),
        newPassword: Yup.string().min(6, t('validation.passwordLength')).required(t('validation.required')),
        confirmNewPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], 'Has�a musz� by� takie same')
            .required(t('validation.required')),
    });

    const usernameValidationSchema = Yup.object({
        newUsername: Yup.string().email(t('validation.emailInvalid')).required(t('validation.required')),
    });

    // --- Funkcje do wysy�ania formularzy ---
    const handleChangePassword = async (values: any, { setSubmitting, resetForm }: FormikHelpers<any>) => {
        try {
            await api.post('/Account/change-password', values);
            showNotification(t('account.changePasswordSuccess'), 'success');
            resetForm();
        } catch (error) {
            showNotification(t('account.changePasswordError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangeUsername = async (values: any, { setSubmitting }: FormikHelpers<any>) => {
        showConfirmation('Po tej operacji zostaniesz wylogowany/a i b�dziesz musia�/a zalogowa� si� ponownie. Kontynuowa�?', async () => {
            try {
                await api.post('/Account/change-username', values);
                showNotification(t('account.changeUsernameSuccess'), 'info');
                logout();
            } catch (error) {
                showNotification(t('account.changeUsernameError'), 'error');
            } finally {
                setSubmitting(false);
            }
        });
    };

    const handleDeleteAccount = async () => {
        showConfirmation(t('account.deleteAccountWarning'), async () => {
            try {
                await api.delete('/Account/delete-account');
                showNotification("Twoje konto zosta�o usuni�te.", 'info');
                logout();
            } catch (error) {
                showNotification("Wyst�pi� b��d podczas usuwania konta.", 'error');
            }
        });
    };

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold text-white">{t('account.pageTitle')}</h1>

            {/* --- Formularz Zmiany Has�a --- */}
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold text-white mb-6">{t('account.changePasswordTitle')}</h2>
                <Formik
                    initialValues={{ oldPassword: '', newPassword: '', confirmNewPassword: '' }}
                    validationSchema={passwordValidationSchema}
                    onSubmit={handleChangePassword}
                >
                    {({ isSubmitting }) => (
                        <Form className="space-y-6">
                            <FormInput name="oldPassword" type="password" label={t('account.oldPassword')} />
                            <FormInput name="newPassword" type="password" label={t('account.newPassword')} />
                            <FormInput name="confirmNewPassword" type="password" label={t('account.confirmNewPassword')} />
                            <div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                >
                                    {isSubmitting ? t('common.loading') : t('account.changePasswordButton')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* --- Formularz Zmiany Nazwy U�ytkownika --- */}
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold text-white mb-6">{t('account.changeUsernameTitle')}</h2>
                <Formik
                    initialValues={{ newUsername: '' }}
                    validationSchema={usernameValidationSchema}
                    onSubmit={handleChangeUsername}
                >
                    {({ isSubmitting }) => (
                        <Form className="space-y-6">
                            <FormInput name="newUsername" type="email" label={t('account.newUsername')} />
                            <div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                >
                                    {isSubmitting ? t('common.loading') : t('account.changeUsernameButton')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* --- Sekcja Usuwania Konta --- */}
            <div className="bg-red-900/20 border border-red-500/30 p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold text-red-400 mb-4">{t('account.deleteAccountTitle')}</h2>
                <p className="text-red-300">{t('account.deleteAccountWarning')}</p>
                <button
                    onClick={handleDeleteAccount}
                    className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                    {t('account.deleteAccountButton')}
                </button>
            </div>
        </div>
    );
};

const AccountPage = () => (
    <Suspense fallback={<div className="text-center p-8 text-white">�adowanie...</div>}>
        <AccountPageContent />
    </Suspense>
);

export default AccountPage;
